import moment from "moment";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

const useDateFormatter = () => {
  const { i18n, t } = useTranslation("dateFormatter");

  const capitalizeFirstLetter = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const getCurrentLocale = useCallback(() => {
    const localeMap = {
      "pt-BR": "pt-br",
      "en-US": "en",
      "es-ES": "es"
    };
    return localeMap[i18n.language] || "pt-br";
  }, [i18n.language]);

  /**
   * Formata uma data para o formato "Dia da semana DD de Mês"
   */
  const formatWeekdayDayMonth = useCallback(
    (date) => {
      if (!date) return "";

      const locale = getCurrentLocale();
      const m = moment(date).locale(locale);
      if (!m.isValid()) return "";

      let weekday = m.format("dddd");
      weekday = capitalizeFirstLetter(weekday).replace("-feira", "");

      const day = m.format("DD");
      const month = capitalizeFirstLetter(m.format("MMM"));

      return t("formatWeekdayDayMonth", {
        weekday,
        day,
        month
      });
    },
    [getCurrentLocale]
  );

  /**
   * Formata uma data para o formato curto de acordo com o locale
   */
  const formatShortDate = useCallback(
    (date) => {
      if (!date) return "";

      const locale = getCurrentLocale();
      const m = moment(date).locale(locale);
      if (!m.isValid()) return "";

      // Moment já formata conforme o locale
      return m.format("L");
    },
    [getCurrentLocale]
  );

  /**
   * Formata uma hora para o formato de 12h ou 24h de acordo com o locale
   */
  const formatTime = useCallback(
    (time, force24h = false) => {
      if (!time) return "";

      const locale = getCurrentLocale();
      
      // Garantir que o time está no formato correto
      let timeStr = time;
      if (typeof time === 'string' && !time.includes(':')) {
        // Se for apenas um número, assumir que são horas
        timeStr = `${time}:00`;
      }
      
      // Cria um momento com a data de hoje e o horário informado
      const today = moment().format("YYYY-MM-DD");
      const m = moment(`${today}T${timeStr}`).locale(locale);
      
      if (!m.isValid()) return "";

      if (force24h) {
        return m.format("HH:mm");
      }

      // Verificar se deve usar formato 12h
      const uses12HourFormat = ["en"].includes(locale);
      
      if (uses12HourFormat) {
        // Formato 12h com AM/PM
        return m.format("h:mm A"); // 'A' maiúsculo para AM/PM em maiúsculas
      } else {
        // Formato 24h
        return m.format("HH:mm");
      }
    },
    [getCurrentLocale]
  );

  /**
   * Formata uma data para o formato completo de acordo com o locale
   */
  const formatFullDate = useCallback(
    (date) => {
      if (!date) return "";

      const locale = getCurrentLocale();
      const m = moment(date).locale(locale);
      if (!m.isValid()) return "";

      // "LL" é o formato longo do moment para o locale
      return m.format("LL");
    },
    [getCurrentLocale]
  );

  /**
   * Formata uma data e hora para o formato completo de acordo com o locale
   */
  const formatDateTime = useCallback(
    (dateTimeStr, force24h = false) => {
      if (!dateTimeStr) return "";

      const locale = getCurrentLocale();
      const m = moment(dateTimeStr).locale(locale);
      if (!m.isValid()) return "";

      const dateFormat = "L";
      const timeFormat = force24h
        ? "HH:mm"
        : ["en"].includes(locale)
        ? "h:mm A"
        : "HH:mm";

      return `${m.format(dateFormat)} ${m.format(timeFormat)}`;
    },
    [getCurrentLocale]
  );

  /**
   * Formata uma data para o formato relativo (hoje, ontem, amanhã, etc.)
   */
  const formatShortTime = useCallback(
    (date) => {
      const m = moment(date);
      if (!m.isValid()) return "";

      const locale = getCurrentLocale();
      m.locale(locale);

      const uses12HourFormat = ["en"].includes(locale);
      return uses12HourFormat ? m.format("h:mm A") : m.format("HH:mm");
    },
    [getCurrentLocale]
  );

  const is24hformat = () => {
    const locale = getCurrentLocale();
    return locale !== "en";
  };

  const formatDateToLanguage = useCallback(() => {
    const language = i18n.language || 'pt-BR';
    
    const formatDate = {
      'en-US': {
        dayMonth: 'MM/DD',
        full: 'MM/DD/YYYY'
      },
      'pt-BR': {
        dayMonth: 'DD/MM',
        full: 'DD/MM/YYYY'
      },
      'es-ES': {
        dayMonth: 'DD/MM',
        full: 'DD/MM/YYYY'
      }
    }

    if (formatDate[language]) {
      return formatDate[language]
    } 
  
    return {
      dayMonth: 'DD/MM',
      full: 'DD/MM/YYYY'
    }
  }, [i18n.language]);

  return {
    formatWeekdayDayMonth,
    formatShortDate,
    formatTime,
    formatFullDate,
    formatDateTime,
    formatShortTime,
    is24hformat,
    formatDateToLanguage
  };
};

export default useDateFormatter;
